/**
 * TemplatePicker — the "+ New → From template…" dialog.
 *
 * Deliberately thin: it lists the templates the person may use and hands
 * the chosen one back (`onSelect`, which instantiates into the CURRENT
 * folder). Creating, editing and deleting templates live on the library
 * page (/templates) — the footer link goes there.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FileText, Globe, Loader2, Lock, Search as SearchIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLoad } from '@/shared/hooks/requests.js';
import { TEMPLATES } from '@/shared/utils/urls.js';
import {
  TEMPLATE_LANGUAGE_LABELS, TEMPLATE_LANGUAGE_SHORT, displayName, languageForUi, orderedVariants, resolveVariant,
} from '@/shared/utils/languages.js';

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'dd.MM.yyyy');
};

export function TemplatePicker({ open, onOpenChange, onSelect }) {
  const { t, i18n } = useTranslation(['my-docs']);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // A language picked on a family row (chip); default = the UI language's member.
  const [picked, setPicked] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useLoad(
    {
      url: TEMPLATES,
      params: { scope: 'visible', size: 100, ...(debounced ? { search: debounced } : {}) },
      showError: false,
    },
    [debounced, open],
  );
  const items = Array.isArray(load.response?.items) ? load.response.items : [];

  const manage = () => {
    onOpenChange(false);
    navigate('/templates');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-4 py-3">
          <DialogTitle className="text-base font-semibold text-foreground">{t('my-docs:templates.pickerTitle')}</DialogTitle>
          <div className="relative mt-2">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('my-docs:templates.search')}
              className="h-9 w-full rounded-[4px] border border-border bg-card pl-8 pr-2 text-sm text-foreground outline-none focus:border-brand"
            />
          </div>
        </DialogHeader>

        <div className="min-h-[160px] flex-1 overflow-y-auto">
          {load.loading && items.length === 0 ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {debounced ? t('my-docs:templates.emptySearch') : t('my-docs:templates.pickerEmpty')}
            </p>
          ) : (
            items.map((tpl) => {
              const variants = orderedVariants(tpl);
              const chosen = picked[tpl.id] || resolveVariant(tpl, i18n.language)?.language || null;
              const language = chosen || languageForUi(tpl, i18n.language);
              const shownName = variants.find((v) => v.language === chosen)?.name || displayName(tpl, i18n.language);
              return (
              <button
                key={tpl.id}
                type="button"
                data-row-id={tpl.id}
                onClick={() => onSelect?.(tpl, language)}
                className="flex w-full items-center gap-3 border-b border-border/60 px-4 py-2.5 text-left hover:bg-muted/40 cursor-pointer"
              >
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-brand/10 text-brand">
                  <FileText className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{shownName}</span>
                  <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>{fmtDate(tpl.updatedAt)}</span>
                    {variants.length > 1 && (
                      <span className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        {variants.map((v) => (
                          <span
                            key={v.id}
                            role="button"
                            tabIndex={0}
                            data-language={v.language || ''}
                            title={TEMPLATE_LANGUAGE_LABELS[v.language] || v.language}
                            onClick={(e) => { e.stopPropagation(); setPicked((prev) => ({ ...prev, [tpl.id]: v.language })); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setPicked((prev) => ({ ...prev, [tpl.id]: v.language })); } }}
                            className={`rounded-[3px] px-1.5 py-0.5 text-[10px] font-semibold leading-none cursor-pointer ${
                              v.language === chosen ? 'bg-brand text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {TEMPLATE_LANGUAGE_SHORT[v.language] || (v.language || '?').toUpperCase()}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </span>
                <span className="flex-none text-muted-foreground" title={t(`my-docs:visibility.${tpl.visibility === 'public' ? 'public' : 'private'}`)}>
                  {tpl.visibility === 'public' ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                </span>
              </button>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border px-4 py-2">
          <span className="text-[11px] text-muted-foreground">{t('my-docs:templates.pickerHint')}</span>
          <button
            type="button"
            onClick={manage}
            className="inline-flex items-center gap-1 rounded-[4px] px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 cursor-pointer"
          >
            {t('my-docs:templates.manage')} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
